import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AsYouType, CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';
import { countries, getCountryData, getEmojiFlag, TCountryCode } from 'countries-list';
import { countryPrefixesAndMasksByIso } from 'src/app/shared/utils/phone-mask.util';

export interface ICountry {
  code: string;
  phone: string;
  flag: string;
  name: string;
  continent: string;
}

@Component({
  selector: 'app-input-phone-number-with-flag',
  templateUrl: './input-phone-number-with-flag.component.html',
  styleUrls: ['./input-phone-number-with-flag.component.scss'],
})
export class InputPhoneNumberWithFlagComponent implements OnInit {
  @Input()
  whatsappNumberInput: string;
  @Input()
  country: string;

  whatsappNumber: string;
  phoneNumber: any;
  selectedCountry: ICountry;
  countriesFlagsPhones: ICountry[] = [];
  phoneErrorMatcher = new PhoneErrorMatcher();
  countrySelectorErrorMatcher = new CountrySelectorErrorMatcher();

  @Output()
  phoneChange = new EventEmitter<string>();

  @Output()
  countryChange = new EventEmitter<string>();

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {
    this.whatsappNumber = this.whatsappNumberInput;
    this.mapCountries();

    if (this.country) {
      this.selectedCountry = this.countriesFlagsPhones.find((c) => c.code === this.country);
    } else {
      this.selectedCountry = {
        name: this.translateService.instant('Shared.Undefined'),
        code: 'globe',
        flag: null,
        continent: null,
        phone: null,
      };
    }

    this.discoverCountryAndSetNumber();
  }

  discoverCountryAndSetNumber = () => {
    if (this.whatsappNumber) {
      try {
        const phoneNumber = parsePhoneNumberWithError(this.whatsappNumber);
        this.selectedCountry = this.countriesFlagsPhones.find((c) => c.phone === `+${phoneNumber.countryCallingCode}`);
        this.whatsappNumber = this.whatsappNumber.replace(this.selectedCountry.phone, '');
        this.onChangeWhatsappNumber();
      } catch (error) {
        this.whatsappNumber = this.whatsappNumber;
      }
    }
  };

  isPhoneInvalidCheck = () => {
    const { isPhoneValid } = this.getFormatted();

    return isPhoneValid != undefined && isPhoneValid === false;
  };

  onChangeCountry = (country: ICountry) => {
    if (country.code === this.selectedCountry?.code) return;

    this.selectedCountry = country;
    this.whatsappNumber = undefined;
    this.phoneErrorMatcher.setExists(false);
    this.countrySelectorErrorMatcher.setExists(false);

    this.countryChange.emit(country.code);
  };

  onChangeWhatsappNumber = () => {
    let { formattedNumber: formatted, isPhoneValid } = this.getFormatted();

    if (formatted?.startsWith(this.selectedCountry?.phone)) {
      formatted = formatted.replace(this.selectedCountry?.phone, '').trim();
    }

    if (this.selectedCountry?.code === 'DO') {
      formatted = formatted.replace('+1 809', '').trim();
    }

    this.whatsappNumber = formatted;

    if (isPhoneValid) {
      this.whatsappNumber = `${this.selectedCountry?.phone} ${this.whatsappNumber}`;
      this.phoneErrorMatcher.setExists(false);
      this.countrySelectorErrorMatcher.setExists(false);
    }

    if (this.whatsappNumber === '') {
      this.phoneErrorMatcher.setExists(false);
      this.countrySelectorErrorMatcher.setExists(false);
      this.whatsappNumber = '';
    }

    this.phoneChange.emit(this.whatsappNumber);
  };

  mapCountries = () => {
    const tmpCountriesPhonesByCodes = [];

    Object.keys(countries).forEach((k) => {
      if (['SA', 'NA'].includes(countries[k].continent)) {
        if (countryPrefixesAndMasksByIso[k]) {
          tmpCountriesPhonesByCodes.push({
            code: k,
            phone: `+${countries[k].phone[0]}`,
            name: countries[k].name,
            continent: countries[k].continent,
          });
        }
      }
    });

    this.countriesFlagsPhones = tmpCountriesPhonesByCodes.map((p) => ({
      code: p.code,
      phone: p.phone,
      flag: getEmojiFlag(p.code as TCountryCode),
      name: p.name,
      continent: p.continent,
    }));

    this.countriesFlagsPhones.push(
      {
        code: 'ES',
        name: getCountryData('ES').name,
        flag: getEmojiFlag('ES'),
        continent: 'EU',
        phone: `+${getCountryData('ES').phone[0]}`,
      },
      {
        code: 'CN',
        name: getCountryData('CN').name,
        flag: getEmojiFlag('CN'),
        continent: 'AS',
        phone: `+${getCountryData('CN').phone[0]}`,
      }
    );

    this.countriesFlagsPhones.sort((a, b) => a.name.localeCompare(b.name));

    this.countriesFlagsPhones.unshift({
      name: this.translateService.instant('Shared.Undefined'),
      code: 'globe',
      flag: null,
      continent: null,
      phone: null,
    });
  };

  validatePhone = () => {
    if (!this.whatsappNumber) {
      this.phoneErrorMatcher.setExists(false);
      this.countrySelectorErrorMatcher.setExists(false);
    } else {
      if (this.isRequiredCountry()) {
        this.phoneErrorMatcher.setExists(true);
        return false;
      }
      if (this.isPhoneInvalidCheck()) {
        this.phoneErrorMatcher.setExists(true);
        return false;
      }
      if (this.isRequiredCountry()) {
        this.countrySelectorErrorMatcher.setExists(true);
        return false;
      }
    }
    return true;
  };

  getFormatted = () => {
    const formatter: any = new AsYouType(this.selectedCountry?.code as CountryCode);
    let formatted = formatter.input(`${this.selectedCountry?.phone} ${this.whatsappNumber}`);
    formatted = formatter.getNumberValue() ? formatter.formattedOutput : this.whatsappNumber;
    const isPhoneValid = formatter.isValid();
    return { formattedNumber: formatted, isPhoneValid };
  };

  isRequiredCountry = () => {
    const { isPhoneValid } = this.getFormatted();

    return (
      this.selectedCountry == null ||
      this.selectedCountry?.code === 'globe' ||
      (!isPhoneValid && this.whatsappNumber !== undefined)
    );
  };

  onPhoneChange = (event: any) => {
    const el: HTMLInputElement = document.getElementById('whatsappNumber') as HTMLInputElement;

    if (event?.data && !/\d/.test(event?.data)) {
      this.whatsappNumber = this.whatsappNumber.replace(event?.data, '');

      el.value = this.whatsappNumber;
      return;
    }

    this.onChangeWhatsappNumber();
  };
}

export class CountrySelectorErrorMatcher implements ErrorStateMatcher {
  CountrySelectorExists = false;

  public getExists(): boolean {
    return this.CountrySelectorExists;
  }

  public setExists(val: boolean) {
    this.CountrySelectorExists = val;
  }

  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return (
      this.CountrySelectorExists ||
      ((control.touched || form.submitted) && control.errors !== undefined && control.errors !== null)
    );
  }
}

export class PhoneErrorMatcher implements ErrorStateMatcher {
  PhoneExists = false;

  public getExists(): boolean {
    return this.PhoneExists;
  }

  public setExists(val: boolean) {
    this.PhoneExists = val;
  }

  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return (
      this.PhoneExists ||
      ((control.touched || form.submitted) && control.errors !== undefined && control.errors !== null)
    );
  }
}
